import * as React from 'react';
import { Quote } from 'lucide-react';
import {
  WorkspaceButton,
  WorkspaceDialog,
  WorkspaceField,
  WorkspaceInput,
  WorkspaceNotice,
  WorkspaceSelect,
  useToast,
} from '@/components/ui/workspace';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { ApiError } from '@/lib/api/client';
import {
  useClientFactsQuery,
  useSaveClientFactsMutation,
  type ClientFact,
  type FactSourceKind,
} from '@/lib/api/client-facts';
import { useDiscoveryDocumentsQuery } from '@/lib/api/discovery';
import { cn } from '@/lib/utils/cn';
import {
  factBody,
  factDraftOf,
  factProblem,
  formatDay,
  plural,
  todayIso,
  withSource,
  withValue,
  type FactDraft,
} from '../discovery-logic';

/**
 * Client facts (T-0185; OQ-W): the client's state, entity type,
 * population, budget, employees and current systems, each with where it
 * came from and the date it is true as of. They live on the client; every
 * engagement of the client reads the same values. Prototype: the Client
 * facts dialog and step 2 of `#setup` in `docs/discovery/prototype/`.
 *
 * Saving a value never edits the old one: the server keeps the history.
 * Typing a new value resets its source to "Entered by hand, as of today"
 * until the person says where it came from, so an old citation never
 * vouches for a new number. Nothing here is filled in by AI.
 */

const KIND_LABEL: Record<FactSourceKind, string> = {
  manual: 'Entered by hand',
  document: 'A document',
  evidence: 'Confirmed evidence',
};

const NOT_EDITABLE =
  'Client facts belong to the client: only people on the client’s team, or a firm admin, can change them.';

function SourceLine({ fact }: { fact: ClientFact }) {
  return (
    <span className="flex min-w-0 items-center gap-1 text-meta text-ink-500">
      <Quote className="h-[11px] w-[11px] flex-none" aria-hidden="true" />
      <span className="truncate">
        {fact.source
          ? `${fact.source.label}${fact.as_of ? ` · as of ${formatDay(fact.as_of)}` : ''}`
          : 'Not set yet'}
      </span>
    </span>
  );
}

/** Read-only facts, for setup step 2: three columns, value and where it came from. */
export function ClientFactsSummary({ engagementId }: { engagementId: string }) {
  const q = useClientFactsQuery(engagementId);
  if (q.error) return <ErrorMessage error={q.error} />;
  if (!q.data) return <Spinner />;
  return (
    <dl className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-3">
      {q.data.facts.map((f) => (
        <div key={f.fact_key} className="min-w-0">
          <dt className="mb-1 text-meta font-medium text-ink-700">{f.label}</dt>
          <dd className={cn('truncate text-body', f.value ? 'text-ink-900' : 'text-ink-300')}>
            {f.value ?? 'Not set'}
          </dd>
          <dd className="mt-1">
            <SourceLine fact={f} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

export interface ClientFactsDialogProps {
  engagementId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ClientFactsDialog({ engagementId, open, onOpenChange }: ClientFactsDialogProps) {
  const q = useClientFactsQuery(engagementId, open);
  const save = useSaveClientFactsMutation(engagementId);
  const { toast } = useToast();
  const [drafts, setDrafts] = React.useState<Record<string, FactDraft>>({});
  const [failure, setFailure] = React.useState<{ key: string; message: string } | null>(null);

  // A fresh draft each time the dialog opens. A refetch while it is open
  // (after a partial save) keeps what the person typed.
  const facts = q.data?.facts;
  const [loaded, setLoaded] = React.useState(false);
  React.useEffect(() => {
    if (!open) {
      setLoaded(false);
      setFailure(null);
    } else if (facts && !loaded) {
      setDrafts(Object.fromEntries(facts.map((f) => [f.fact_key, factDraftOf(f)])));
      setLoaded(true);
    }
  }, [open, facts, loaded]);

  const canEdit = q.data?.can_edit ?? false;
  const today = todayIso();
  const dirty = (facts ?? []).filter((f) => drafts[f.fact_key]?.dirty);
  const problems = Object.fromEntries(
    dirty.map((f) => [f.fact_key, factProblem(drafts[f.fact_key]!, today)]),
  );
  const blocked = dirty.length === 0 || Object.values(problems).some(Boolean);
  const anyDocument = Object.values(drafts).some((d) => d.editingSource && d.kind === 'document');
  const docs = useDiscoveryDocumentsQuery(engagementId, open && anyDocument);

  const update = (key: string, next: (d: FactDraft) => FactDraft) =>
    setDrafts((prev) => (prev[key] ? { ...prev, [key]: next(prev[key]!) } : prev));

  const onSave = () => {
    setFailure(null);
    save.mutate(
      dirty.map((f) => ({ factKey: f.fact_key, body: factBody(drafts[f.fact_key]!, f) })),
      {
        onSuccess: (saved) => {
          toast({
            message: `${plural(saved.length, 'client fact')} saved. The earlier values stay in the history.`,
          });
          onOpenChange(false);
        },
        onError: (error) => {
          const key = (error as Error & { factKey?: string }).factKey ?? '';
          const saved = (error as Error & { saved?: string[] }).saved ?? [];
          // What was saved is clean now; the rest stays as typed.
          setDrafts((prev) => {
            const next = { ...prev };
            for (const k of saved)
              if (next[k]) next[k] = { ...next[k]!, dirty: false, editingSource: false };
            return next;
          });
          setFailure({
            key,
            message: error instanceof ApiError ? error.message : 'The fact could not be saved.',
          });
        },
      },
    );
  };

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Client facts"
      description="Typed by a consultant, each with where it came from. They decide which statutory packs and vendors apply later."
      widthClassName="w-[680px]"
      closeOnScrimClick={dirty.length === 0}
      actions={
        canEdit ? (
          <>
            <WorkspaceButton size="md" variant="tertiary" onClick={() => onOpenChange(false)}>
              Cancel
            </WorkspaceButton>
            <WorkspaceButton
              size="md"
              variant="primary"
              disabled={blocked || save.isPending}
              onClick={onSave}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </WorkspaceButton>
          </>
        ) : (
          <WorkspaceButton size="md" onClick={() => onOpenChange(false)}>
            Close
          </WorkspaceButton>
        )
      }
    >
      {q.error ? <ErrorMessage error={q.error} /> : null}
      {!q.data && !q.error ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading the client’s facts…
        </div>
      ) : null}
      {q.data && !canEdit ? <WorkspaceNotice>{NOT_EDITABLE}</WorkspaceNotice> : null}
      {q.data ? (
        <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
          {q.data.facts.map((f) => {
            const d = drafts[f.fact_key];
            if (!d) return null;
            const problem =
              failure?.key === f.fact_key ? failure.message : d.dirty ? problems[f.fact_key] : null;
            return (
              <FactEditor
                key={f.fact_key}
                fact={f}
                draft={d}
                problem={problem ?? null}
                disabled={!canEdit || save.isPending}
                documents={docs.data?.documents ?? []}
                documentsLoading={docs.isLoading}
                today={today}
                onValue={(value) => update(f.fact_key, (x) => withValue(x, value, today))}
                onSource={(patch) => update(f.fact_key, (x) => withSource(x, patch))}
                onChangeSource={() => update(f.fact_key, (x) => ({ ...x, editingSource: true }))}
              />
            );
          })}
        </div>
      ) : null}
    </WorkspaceDialog>
  );
}

function FactEditor({
  fact,
  draft: d,
  problem,
  disabled,
  documents,
  documentsLoading,
  today,
  onValue,
  onSource,
  onChangeSource,
}: {
  fact: ClientFact;
  draft: FactDraft;
  problem: string | null;
  disabled: boolean;
  documents: ReadonlyArray<{ id: string; name: string }>;
  documentsLoading: boolean;
  today: string;
  onValue: (value: string) => void;
  onSource: (patch: Partial<FactDraft>) => void;
  onChangeSource: () => void;
}) {
  return (
    <div className="min-w-0" data-fact={fact.fact_key}>
      <WorkspaceField
        label={fact.label}
        error={problem}
        help={
          d.editingSource ? null : (
            <span className="flex min-w-0 items-center gap-2">
              <SourceLine fact={fact} />
              {!disabled ? (
                <button
                  type="button"
                  onClick={onChangeSource}
                  className="flex-none font-medium text-ink-700 underline underline-offset-[3px] hover:text-ink-900"
                  aria-label={`Change the source of ${fact.label}`}
                >
                  Change source
                </button>
              ) : null}
            </span>
          )
        }
      >
        {(id, describedBy) => (
          <WorkspaceInput
            id={id}
            aria-describedby={describedBy}
            value={d.value}
            disabled={disabled}
            placeholder="Not set"
            onChange={(e) => onValue(e.target.value)}
          />
        )}
      </WorkspaceField>
      {d.editingSource && !disabled ? (
        <div
          role="group"
          aria-label={`Where ${fact.label} came from`}
          className="mt-2 grid grid-cols-2 gap-2 rounded-card border border-ink-100 bg-paper-50 p-2.5"
        >
          <WorkspaceField label="Source" className="col-span-2">
            {(id) => (
              <WorkspaceSelect
                id={id}
                value={d.kind}
                onChange={(e) => onSource({ kind: e.target.value as FactSourceKind })}
              >
                {(Object.keys(KIND_LABEL) as FactSourceKind[]).map((k) => (
                  <option key={k} value={k}>
                    {KIND_LABEL[k]}
                  </option>
                ))}
              </WorkspaceSelect>
            )}
          </WorkspaceField>
          {d.kind === 'document' ? (
            <>
              <WorkspaceField label="Document">
                {(id) => (
                  <WorkspaceSelect
                    id={id}
                    value={d.documentId}
                    onChange={(e) => onSource({ documentId: e.target.value })}
                  >
                    <option value="">
                      {documentsLoading
                        ? 'Loading…'
                        : documents.length
                          ? 'Pick one'
                          : 'No documents yet'}
                    </option>
                    {documents.map((doc) => (
                      <option key={doc.id} value={doc.id}>
                        {doc.name}
                      </option>
                    ))}
                  </WorkspaceSelect>
                )}
              </WorkspaceField>
              <WorkspaceField label="Page">
                {(id) => (
                  <WorkspaceInput
                    id={id}
                    inputMode="numeric"
                    value={d.page}
                    placeholder="Optional"
                    onChange={(e) => onSource({ page: e.target.value })}
                  />
                )}
              </WorkspaceField>
            </>
          ) : d.kind === 'evidence' ? (
            <WorkspaceField label="Evidence number">
              {(id) => (
                <WorkspaceInput
                  id={id}
                  value={d.evidenceNumber}
                  placeholder="E-012"
                  onChange={(e) => onSource({ evidenceNumber: e.target.value })}
                />
              )}
            </WorkspaceField>
          ) : (
            <WorkspaceField label="Note">
              {(id) => (
                <WorkspaceInput
                  id={id}
                  value={d.note}
                  placeholder="e.g. Kickoff call"
                  onChange={(e) => onSource({ note: e.target.value })}
                />
              )}
            </WorkspaceField>
          )}
          <WorkspaceField label="As of">
            {(id) => (
              <WorkspaceInput
                id={id}
                type="date"
                max={today}
                value={d.asOf}
                onChange={(e) => onSource({ asOf: e.target.value })}
              />
            )}
          </WorkspaceField>
        </div>
      ) : null}
    </div>
  );
}
